import React, { useCallback } from 'react';
import { ResponsiveLine, type PointTooltipProps } from '@nivo/line';
import { TLineGraph } from 'pages/Analytics/types';
import ChartTooltip from './ChartTooltip';
import './Graphs.scss';

type TLineGraphProps = {
  responseData: TLineGraph;
};

const LineGraph: React.FC<TLineGraphProps> = ({ responseData }) => {
  const { xAxis, yAxis } = responseData.moduleConfig.chartConfig;
  const xLabel = xAxis || 'Time';
  const yLabel = yAxis || 'Value';

  const transformedData: { id: string; data: { x: Date; y: number }[] }[] = [];
  responseData.data.forEach((rawDatum) => {
    const index = transformedData.findIndex((td) => td.id === rawDatum.name);

    if (index === -1)
      transformedData.push({ id: rawDatum.name, data: [{ x: new Date(rawDatum.x * 1000), y: rawDatum.y }] });
    else {
      transformedData[index].data.push({ x: new Date(rawDatum.x * 1000), y: rawDatum.y });
    }
  });

  const renderTooltip = useCallback(
    ({ point }: PointTooltipProps) => (
      <ChartTooltip
        xLabel={xLabel}
        xValue={point.data.xFormatted}
        yLabel={yLabel}
        yValue={point.data.yFormatted}
        color={point.seriesColor}
      />
    ),
    [xLabel, yLabel],
  );

  return (
    <div className="custom-analytics-graph">
      <ResponsiveLine
        data={transformedData}
        margin={{ top: 24, right: 16, bottom: 40, left: 50 }}
        xScale={{ type: 'time', format: `'%y, %b %d` }}
        yScale={{
          type: 'linear',
          stacked: true,
        }}
        xFormat={`time:%Y %b %d`}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 5,
          tickValues: 5,
          legend: xLabel,
          legendOffset: 32,
          legendPosition: 'middle',
          // TODO (PET-3697): Figure out with design what we want this label to look like based on the interval
          format: `%m-%d '%y`,
          truncateTickAt: 0,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 5,
          tickValues: 5,
          legend: yLabel,
          legendOffset: -40,
          legendPosition: 'middle',
        }}
        colors={'#0851df'}
        enableArea={true}
        enablePoints={false}
        enableGridY={false}
        enableGridX={false}
        useMesh={true}
        tooltip={renderTooltip}
      />
    </div>
  );
};

export default LineGraph;
